import {
  DEPARTMENTS,
  DEFAULT_DEPARTMENT,
  TECH_TEAM_DEPARTMENT,
  normalizeDepartment,
} from "./departments";

export const DEFAULT_DEPARTMENT_WORKFLOW = {
  [TECH_TEAM_DEPARTMENT]: {
    sprintsEnabled: true,
    boardLabel: "Sprint Board",
    cycleLabel: "Sprint",
  },
  Marketing: {
    sprintsEnabled: false,
    boardLabel: "Sprint Board",
    cycleLabel: "Phase",
  },
  CSM: {
    sprintsEnabled: false,
    boardLabel: "Sprint Board",
    cycleLabel: "Cycle",
  },
  IT: {
    sprintsEnabled: false,
    boardLabel: "Sprint Board",
    cycleLabel: "Phase",
  },
  HR: {
    sprintsEnabled: false,
    boardLabel: "Sprint Board",
    cycleLabel: "Phase",
  },
};

/**
 * Menu entries a department may show, hide or rename.
 *
 * Mirrors backend/constants/departmentWorkflow.js — the backend copy is what
 * persists, so the two lists must stay identical.
 *
 * This is presentation only. Whether someone may *open* a route is decided by
 * role permissions and enforced server-side; hiding an entry here removes it
 * from the sidebar but grants nothing and blocks nothing. Anything omitted
 * from this list (Settings, Notifications) is always shown to whoever has the
 * permission for it, so a department can never hide itself out of its own
 * configuration screen.
 */
export const CONFIGURABLE_NAV = [
  { path: "/dashboard", label: "Dashboard" },
  { path: "/projects", label: "All Projects" },
  { path: "/tracker", label: "Tracker" },
  { path: "/budget", label: "Budget" },
  { path: "/expense-claims", label: "Expense Claims" },
  { path: "/sprint-board", label: "Sprint Board" },
  { path: "/daily-notebook", label: "Daily Notebook" },
  { path: "/issues", label: "Issues" },
  { path: "/team-activity", label: "Team Activity" },
  { path: "/my-tasks", label: "My Tasks" },
  { path: "/developer-activity", label: "Developer Activity" },
  { path: "/team-leave", label: "Leave calendar" },
];

export const NAV_LABEL_MAX = 40;

export const CONFIGURABLE_NAV_PATHS = CONFIGURABLE_NAV.map((n) => n.path);

/**
 * Per-department nav defaults. Everything visible with its stock label, except
 * where a department rule already hid it before this config existed — Issues
 * is Tech-only, and Daily Notebook only appears when sprints are off. Those
 * defaults keep the sidebar identical to what each department sees today.
 */
export function defaultNavForDepartment(department) {
  const dept = normalizeDepartment(department);
  const isTech = dept === TECH_TEAM_DEPARTMENT;
  const sprintsOn = DEFAULT_DEPARTMENT_WORKFLOW[dept]?.sprintsEnabled ?? isTech;

  const nav = {};
  for (const item of CONFIGURABLE_NAV) {
    let visible = true;
    if (item.path === "/issues") visible = isTech;
    if (item.path === "/daily-notebook") visible = !sprintsOn;
    nav[item.path] = { visible, label: item.label };
  }
  return nav;
}

export function mergeNavConfig(storedNav, department) {
  const base = defaultNavForDepartment(department);
  const merged = {};
  for (const item of CONFIGURABLE_NAV) {
    const custom = storedNav?.[item.path] || {};
    const fallback = base[item.path];
    merged[item.path] = {
      visible: custom.visible !== undefined ? !!custom.visible : fallback.visible,
      // An empty rename falls back to the stock label rather than showing a
      // blank menu row; the cap keeps a pasted essay from breaking the sidebar.
      label: String(custom.label ?? "").trim().slice(0, NAV_LABEL_MAX) || fallback.label,
    };
  }
  return merged;
}

/**
 * Normalise a stored order into a complete, duplicate-free path list.
 *
 * Unknown paths are dropped and missing ones appended in catalog order, so a
 * stale saved order (written before a menu item existed) still yields every
 * current item exactly once. Used for both the department order and a user's
 * personal order — same guarantees either way.
 */
export function normalizeNavOrder(storedOrder) {
  const known = new Set(CONFIGURABLE_NAV_PATHS);
  const seen = new Set();
  const ordered = [];
  for (const path of Array.isArray(storedOrder) ? storedOrder : []) {
    if (known.has(path) && !seen.has(path)) {
      seen.add(path);
      ordered.push(path);
    }
  }
  for (const path of CONFIGURABLE_NAV_PATHS) {
    if (!seen.has(path)) ordered.push(path);
  }
  return ordered;
}

/**
 * Sort nav items by a path order, keeping anything the order doesn't mention
 * (Settings, Notifications, User Management) in its original relative place.
 * Those unmanaged items keep their existing positions rather than being pushed
 * to one end.
 */
export function applyNavOrder(items, order) {
  const rank = new Map(normalizeNavOrder(order).map((p, i) => [p, i]));
  const managed = [];
  const slots = [];
  items.forEach((item, index) => {
    if (rank.has(item.path)) {
      managed.push(item);
      slots.push(index);
    }
  });
  managed.sort((a, b) => rank.get(a.path) - rank.get(b.path));
  const out = [...items];
  slots.forEach((slot, i) => {
    out[slot] = managed[i];
  });
  return out;
}

export function defaultWorkflowForDepartment(department) {
  const dept = normalizeDepartment(department);
  const base = DEFAULT_DEPARTMENT_WORKFLOW[dept]
    ? { ...DEFAULT_DEPARTMENT_WORKFLOW[dept] }
    : {
        sprintsEnabled: dept === TECH_TEAM_DEPARTMENT,
        boardLabel: "Sprint Board",
        cycleLabel: dept === TECH_TEAM_DEPARTMENT ? "Sprint" : "Phase",
      };
  return {
    ...base,
    nav: defaultNavForDepartment(dept),
    navOrder: [...CONFIGURABLE_NAV_PATHS],
  };
}

export function mergeWorkflowConfig(stored) {
  const merged = {};
  for (const dept of DEPARTMENTS) {
    const base = defaultWorkflowForDepartment(dept);
    const custom = stored?.[dept] || stored?.[normalizeDepartment(dept)] || {};
    merged[dept] = {
      sprintsEnabled: custom.sprintsEnabled !== undefined ? !!custom.sprintsEnabled : base.sprintsEnabled,
      boardLabel: String(custom.boardLabel || base.boardLabel).trim() || base.boardLabel,
      cycleLabel: String(custom.cycleLabel || base.cycleLabel).trim() || base.cycleLabel,
      nav: mergeNavConfig(custom.nav, dept),
      navOrder: normalizeNavOrder(custom.navOrder),
    };
  }
  return merged;
}

/**
 * The nav overrides for a department: `{ [path]: { visible, label } }`.
 * Always a complete map, so callers never handle a missing key.
 */
export function getDepartmentNavConfig(department, workflowMap) {
  const resolved = resolveDepartmentWorkflow(department, workflowMap);
  return resolved.nav || defaultNavForDepartment(department);
}

/** The department's saved menu order, always a complete path list. */
export function getDepartmentNavOrder(department, workflowMap) {
  const resolved = resolveDepartmentWorkflow(department, workflowMap);
  return normalizeNavOrder(resolved.navOrder);
}

export function resolveDepartmentWorkflow(department, workflowMap) {
  const dept = normalizeDepartment(department || DEFAULT_DEPARTMENT);
  const map =
    workflowMap && Object.keys(workflowMap).length > 0
      ? workflowMap
      : mergeWorkflowConfig({});
  return map[dept] || defaultWorkflowForDepartment(dept);
}

export function departmentUsesSprints(department, workflowMap) {
  return resolveDepartmentWorkflow(department, workflowMap).sprintsEnabled !== false;
}

/**
 * The board's display name for a department. Reads the nav override so a
 * rename in Settings actually reaches the page heading — previously this
 * ignored its arguments and always returned "Sprint Board", which is why the
 * label field looked editable but changed nothing.
 */
export function getWorkBoardLabel(department, workflowMap) {
  const nav = getDepartmentNavConfig(department, workflowMap);
  return nav["/sprint-board"]?.label || "Sprint Board";
}

export function getCycleLabel(department, workflowMap) {
  return resolveDepartmentWorkflow(department, workflowMap).cycleLabel || "Sprint";
}

export function formatCycleLabel(template, cycleLabel) {
  const label = cycleLabel || "Sprint";
  return String(template || "").replace(/\{cycle\}/gi, label).replace(/Sprint/g, label);
}
